import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import Divider from 'LuxKit/Divider'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import LoyaltyBenefitIcon from 'loyalty/components/LoyaltyBenefitIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { useAppSelector } from 'hooks/reduxHooks'
import { getFirstName } from 'selectors/accountSelectors'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { CONFETTI_COLOURS, CONFETTI_AUTO_RUN } from 'loyalty/constants/loyaltyConfetti'
import type { ComponentProps } from 'react'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import RealisticConfetti from 'react-canvas-confetti/dist/presets/realistic'
import styled from 'styled-components'
import { rem } from 'polished'
import { tierGradientBackgroundStyle, tierLightContrastColorStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

const GradientHeader = styled.div`
  padding: ${rem(32)} ${rem(28)};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const TierStatusText = styled.span`
  ${tierLightContrastColorStyle}
`

const LogoRow = styled.div`
  display: flex;
  align-items: center;
`

const ContentSection = styled.div`
  padding: ${rem(24)} ${rem(24)} 0;
`

const BenefitRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  padding: ${rem(8)} 0;
`

interface Props {
  newTier: App.LoyaltyTier
}

function getBenefitLabel(
  benefit: App.LoyaltyRegionTierBenefit,
  intl: ReturnType<typeof useIntl>,
): string | undefined {
  switch (benefit.code) {
    case 'hotel_upgrade':
      return intl.formatMessage({
        id: 'zDnbNHlI6DrNSAF8BiYr',
        defaultMessage: 'Free room or package upgrade',
        description: 'Benefit label for room upgrade in tier upgrade success modal',
      })
    case 'airport_transfer':
      return intl.formatMessage({
        id: 'SbdxRIEmNbsQ9MK9yuE0',
        defaultMessage: 'Free airport chauffeur service',
        description: 'Benefit label for airport transfer in success modal',
      })
    case 'lounge_pass':
      return intl.formatMessage({
        id: 'SLzn_6NE6x_jb6TPA58G',
        defaultMessage: 'Free airport lounge passes',
        description: 'Benefit label for lounge pass in tier upgrade success modal',
      })
    case 'tour_exp_credit':
      return intl.formatMessage({
        id: 'igs5lKbgfLO25DhsW0nP',
        defaultMessage: 'Tour experience credit',
        description: 'Benefit label for tour credit in tier upgrade success modal',
      })
    case 'travelshoot_credit':
      return intl.formatMessage({
        id: 'OVQpqRkrC7KjD7LpM0yN',
        defaultMessage: 'TravelShoot voucher',
        description: 'Benefit label for travelshoot credit in tier upgrade success modal',
      })
    case 'phone_support':
      return intl.formatMessage({
        id: 'UytZvdS0FgQdFZkUtXCR',
        defaultMessage: '24/7 priority phone support',
        description: 'Benefit label for phone support in tier upgrade success modal',
      })
    case 'discounted_insurance':
      return intl.formatMessage({
        id: 'A98xDAdrR5rX0kN_9GM1',
        defaultMessage: 'Discounted travel insurance',
        description: 'Benefit label for discounted insurance in success modal',
      })
    default:
      return undefined
  }
}

function getBenefitCountLabel(benefit: App.LoyaltyRegionTierBenefit, intl: ReturnType<typeof useIntl>): string {
  if (benefit.unlimited) {
    return intl.formatMessage({
      id: '78l4XMAU_1mRohHOmBny',
      defaultMessage: 'Unlimited',
      description: 'Label for unlimited benefit count in tier upgrade success modal',
    })
  }
  return `${benefit.limit ?? 0}x`
}

function TierUpgradeCampaignSuccessModal(props: Props) {
  const { newTier } = props
  const intl = useIntl()
  const firstName = useAppSelector(getFirstName)
  const [loyaltyConfig] = useLoyaltyConfig()
  const programName = getLoyaltyProgramDisplayName()
  const tierName = getLoyaltyTierDisplayName(newTier)
  const benefits = loyaltyConfig?.regionTierBenefits[newTier] ?? []

  const confettiColours = CONFETTI_COLOURS[newTier]
  const confettiDecoration = useCallback<NonNullable<ComponentProps<typeof RealisticConfetti>['decorateOptions']>>((options) => {
    return {
      ...options,
      colors: confettiColours,
      origin: { y: 0.45, x: 0.5 },
      zIndex: 999,
    }
  }, [confettiColours])

  const welcomeText = intl.formatMessage({
    id: 'pvy0FRwcxTnILdVwU5m4',
    defaultMessage: '{name}, welcome to',
    description: 'Personalised welcome text on the tier upgrade success modal',
  }, { name: firstName })

  const tierStatusText = `${tierName} ${programName} Status`

  const descriptionText = intl.formatMessage({
    id: 'MGHNjBUOwvg24c6m0-a_',
    defaultMessage: 'Your complimentary {tierName} membership has been successfully activated and will be available for 12 months from today.',
    description: 'Membership activation description on the tier upgrade success modal',
  }, { tierName })

  const topBenefitsHeading = intl.formatMessage({
    id: '8Or1qdEIttZblMftf00f',
    defaultMessage: 'Your top benefits',
    description: 'Heading for benefits list on tier upgrade success modal',
  })

  const exploreText = intl.formatMessage({
    id: 'frt8OND4xF6BgIs2GaAY',
    defaultMessage: 'Explore your membership',
    description: 'Button text to explore membership on tier upgrade success modal',
  })

  const tierClass = `tier-${newTier}`

  return <ModalBase size="S" dismissible>
    <GradientHeader className={tierClass}>
      <VerticalSpacer gap={24}>
        <LogoRow>
          <LuxuryEscapesSmallLogo height={24} fill="currentColor" />
          <LoyaltyProgramNameLogo width={123} height={24} />
        </LogoRow>
        <div>
          <Heading variant="heading2">{welcomeText}</Heading>
          <Heading variant="heading2"><TierStatusText className={tierClass}>{tierStatusText}</TierStatusText></Heading>
        </div>
      </VerticalSpacer>
    </GradientHeader>
    <ModalBody>
      <ContentSection>
        <VerticalSpacer gap={20}>
          <BodyText variant="medium">{descriptionText}</BodyText>
          <VerticalSpacer gap={0}>
            <BodyTextBlock variant="medium" weight="bold">{topBenefitsHeading}</BodyTextBlock>
            {benefits.map((benefit, index) => {
              const label = getBenefitLabel(benefit, intl)
              if (!label) return null
              return <React.Fragment key={benefit.code}>
                {index > 0 && <Divider kind="primary" />}
                <BenefitRow>
                  <BodyTextBlock
                    variant="medium"
                    startIcon={<LoyaltyBenefitIcon benefitType={benefit.code} />}
                  >
                    {label}
                  </BodyTextBlock>
                  <UrgencyLabel variant="positive">
                    {getBenefitCountLabel(benefit, intl)}
                  </UrgencyLabel>
                </BenefitRow>
              </React.Fragment>
            })}
          </VerticalSpacer>
        </VerticalSpacer>
      </ContentSection>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: exploreText,
        to: `/${LOYALTY_LANDING_PAGE}`,
      }}
    />
    <RealisticConfetti autorun={CONFETTI_AUTO_RUN} decorateOptions={confettiDecoration} />
  </ModalBase>
}

export default TierUpgradeCampaignSuccessModal
